import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FileText,
  Loader,
  CreditCard as Edit3,
  Save,
  X,
  Check,
  Database,
  Cloud,
  AlertCircle,
  CheckCircle,
  Tag,
  Plus,
  Braces,
} from 'lucide-react';
import {
  formatEdgeFunctionInvokeError,
  formatUnknownError,
  getUserAccessTokenForFunctions,
  invokeMarkdownAlgoliaEdge,
  parseMarkdownAlgoliaStatus,
  parseMarkdownIndexedStatus,
  supabase,
  type MarkdownFile,
  type MarkdownIndexedStatus,
} from '../lib/supabase';
import { SUGGESTED_NONPROFIT_KNOWLEDGE_TAG_NAMES } from '../lib/suggestedKnowledgeTags';

interface DocumentViewerProps {
  documentId: string;
  onBack: () => void;
}

export function DocumentViewer({ documentId }: DocumentViewerProps) {
  const [document, setDocument] = useState<MarkdownFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const isEditModeRef = useRef(false);
  isEditModeRef.current = isEditMode;
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editState, setEditState] = useState('');
  const [editJurisdiction, setEditJurisdiction] = useState('');
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isIndexing, setIsIndexing] = useState(false);
  const [isIndexingAlgolia, setIsIndexingAlgolia] = useState(false);
  const [indexBanner, setIndexBanner] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [showJsonModal, setShowJsonModal] = useState(false);

  /** @param soft When true, skip full-page loading state (use after index actions so the UI keeps showing). */
  const loadDocument = useCallback(
    async (options?: { soft?: boolean }): Promise<MarkdownFile | null> => {
      const soft = options?.soft === true;
      if (!soft) {
        setLoading(true);
        setError(null);
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('markdown_files')
          .select('*')
          .eq('id', documentId)
          .maybeSingle();

        if (fetchError) {
          if (!soft) setError('Failed to load document');
          console.error('Error loading document:', fetchError);
          return null;
        }
        if (!data) {
          if (!soft) setError('Document not found');
          return null;
        }

        setDocument(data);
        setEditTitle(data.title);
        setEditContent(data.content);
        setEditTags(data.tags || []);
        setEditState(data.state || '');
        setEditJurisdiction(data.jurisdiction || '');
        return data;
      } finally {
        if (!soft) setLoading(false);
      }
    },
    [documentId],
  );

  useEffect(() => {
    void loadDocument();

    const channel = supabase
      .channel(`markdown_files_detail:${documentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'markdown_files',
          filter: `id=eq.${documentId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setDocument(null);
            setError('This document was removed.');
            setLoading(false);
            return;
          }

          const row = payload.new as MarkdownFile | null;
          if (!row?.id) return;

          if (isEditModeRef.current) {
            setDocument((prev) =>
              prev
                ? {
                    ...prev,
                    indexed: row.indexed,
                    algolia: row.algolia,
                    summary: row.summary ?? prev.summary,
                    updated_at: row.updated_at,
                  }
                : row,
            );
          } else {
            setDocument(row);
            setEditTitle(row.title);
            setEditContent(row.content);
            setEditTags(row.tags || []);
            setEditState(row.state || '');
            setEditJurisdiction(row.jurisdiction || '');
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [documentId, loadDocument]);

  const handleSave = async () => {
    if (!document || !editTitle.trim()) {
      alert('Please provide a title');
      return;
    }

    setIsSaving(true);

    const { error: updateError } = await supabase
      .from('markdown_files')
      .update({
        title: editTitle,
        content: editContent,
        tags: editTags,
        state: editState || null,
        jurisdiction: editJurisdiction || null,
        updated_at: new Date().toISOString(),
        indexed: 'no',
        algolia: 'no',
      })
      .eq('id', document.id);

    if (updateError) {
      console.error('Error updating document:', updateError);
      alert('Failed to update document');
    } else {
      await loadDocument();
      setIsEditMode(false);
      setShowTagInput(false);
      setNewTag('');
      const algolia = await invokeMarkdownAlgoliaEdge(document.id);
      if (!algolia.ok) {
        setIndexBanner({
          type: 'error',
          message: `Saved, but search index did not update: ${algolia.message}`,
        });
      } else {
        await loadDocument({ soft: true });
        if (algolia.document) {
          setDocument((prev) =>
            prev ? ({ ...prev, ...algolia.document } as MarkdownFile) : null,
          );
        }
      }
    }

    setIsSaving(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(document?.title || '');
    setEditContent(document?.content || '');
    setEditTags(document?.tags || []);
    setEditState(document?.state || '');
    setEditJurisdiction(document?.jurisdiction || '');
    setShowTagInput(false);
    setNewTag('');
    setIsEditMode(false);
  };

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && !editTags.includes(trimmedTag)) {
      setEditTags([...editTags, trimmedTag]);
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter((tag) => tag !== tagToRemove));
  };

  const handleAddSuggestedTag = (tag: string) => {
    if (!editTags.includes(tag)) {
      setEditTags([...editTags, tag]);
    }
  };

  const handleIndexDocument = async () => {
    if (!document) return;

    setIsIndexing(true);
    setIndexBanner(null);

    try {
      const accessToken = await getUserAccessTokenForFunctions();

      const { data: result, error: invokeError, response: fnResponse } =
        await supabase.functions.invoke<{
          chunksProcessed?: number;
          error?: string;
          message?: string;
        }>('index-document', {
          body: { documentId: document.id },
          headers: { Authorization: `Bearer ${accessToken}` },
        });

      if (invokeError) {
        const detail = await formatEdgeFunctionInvokeError(invokeError, fnResponse);
        await loadDocument({ soft: true });
        setIndexBanner({ type: 'error', message: detail || 'Failed to vectorize document' });
        return;
      }

      if (result && typeof result === 'object' && 'error' in result && result.error) {
        await loadDocument({ soft: true });
        setIndexBanner({
          type: 'error',
          message: String(result.error),
        });
        return;
      }

      await loadDocument({ soft: true });
      setIndexBanner({
        type: 'success',
        message: `Vectorized successfully. Processed ${result?.chunksProcessed ?? 0} chunk(s).`,
      });
    } catch (err) {
      console.error('Error indexing document:', err);
      await loadDocument({ soft: true });
      setIndexBanner({
        type: 'error',
        message: formatUnknownError(err),
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const handleIndexToAlgolia = async () => {
    if (!document) return;

    setIsIndexingAlgolia(true);
    setIndexBanner(null);

    try {
      const result = await invokeMarkdownAlgoliaEdge(document.id);

      if (!result.ok) {
        await loadDocument({ soft: true });
        setIndexBanner({
          type: 'error',
          message: result.message || 'Failed to push document to Algolia',
        });
        return;
      }

      await loadDocument({ soft: true });
      if (result.document) {
        setDocument((prev) =>
          prev ? ({ ...prev, ...result.document } as MarkdownFile) : null,
        );
      }
      setIndexBanner({
        type: 'success',
        message: result.message ?? 'Document pushed to Algolia successfully.',
      });
    } catch (err) {
      console.error('Error indexing to Algolia:', err);
      await loadDocument({ soft: true });
      setIndexBanner({
        type: 'error',
        message: formatUnknownError(err),
      });
    } finally {
      setIsIndexingAlgolia(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex items-center justify-center py-20">
          <Loader className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            {error || 'Document not found'}
          </h2>
          <p className="text-slate-500">The document you're looking for could not be loaded.</p>
        </div>
      </div>
    );
  }

  const indexedStatus: MarkdownIndexedStatus = parseMarkdownIndexedStatus(
    document.indexed,
  );
  const algoliaStatus: MarkdownIndexedStatus = parseMarkdownAlgoliaStatus(
    document.algolia,
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 dark:text-slate-100">
      {indexBanner && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 flex gap-3 items-start shadow-sm ${
            indexBanner.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-green-50 border-green-200 text-green-900'
          }`}
          role="alert"
        >
          {indexBanner.type === 'error' ? (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" />
          ) : (
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" />
          )}
          <p className="text-sm leading-relaxed flex-1 min-w-0 whitespace-pre-wrap break-words">
            {indexBanner.message}
          </p>
          <button
            type="button"
            onClick={() => setIndexBanner(null)}
            className={`flex-shrink-0 p-1 rounded hover:bg-black/5 ${
              indexBanner.type === 'error' ? 'text-red-700' : 'text-green-800'
            }`}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm dark:border dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-200 bg-slate-50 px-8 py-6 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <FileText className="w-8 h-8 text-slate-600 dark:text-slate-400 flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                {isEditMode ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="text-3xl font-bold text-slate-800 dark:text-slate-100 dark:bg-slate-900 mb-2 w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2 break-words">
                    {document.title}
                  </h1>
                )}
                <div className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span>Created: {new Date(document.created_at).toLocaleDateString()}</span>
                    {document.updated_at && document.updated_at !== document.created_at && (
                      <span>Updated: {new Date(document.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={
                        algoliaStatus === 'yes'
                          ? 'inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : algoliaStatus === 'pending'
                            ? 'inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
                            : algoliaStatus === 'failed'
                              ? 'inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-900 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-200'
                              : 'inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      }
                      title={
                        algoliaStatus === 'yes'
                          ? 'Record synced to Algolia'
                          : algoliaStatus === 'pending'
                            ? 'Algolia sync in progress'
                            : algoliaStatus === 'failed'
                              ? 'Last Algolia sync failed'
                              : 'Not synced to Algolia'
                      }
                    >
                      {algoliaStatus === 'yes' ? (
                        <Check className="h-3.5 w-3.5 shrink-0 stroke-[2.5]" aria-hidden />
                      ) : algoliaStatus === 'pending' ? (
                        <Loader className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                      ) : (
                        <X className="h-3.5 w-3.5 shrink-0 stroke-[2.5]" aria-hidden />
                      )}
                      {algoliaStatus === 'yes'
                        ? 'Algolia'
                        : algoliaStatus === 'pending'
                          ? 'Algolia pending'
                          : algoliaStatus === 'failed'
                            ? 'Algolia failed'
                            : 'Algolia off'}
                    </span>
                    <span
                      className={
                        indexedStatus === 'yes'
                          ? 'inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : indexedStatus === 'pending'
                            ? 'inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
                            : indexedStatus === 'failed'
                              ? 'inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-900 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-200'
                              : 'inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      }
                      title={
                        indexedStatus === 'yes'
                          ? 'Vectorized for chat (embeddings ready)'
                          : indexedStatus === 'pending'
                            ? 'Vectorization in progress'
                            : indexedStatus === 'failed'
                              ? 'Last vectorization attempt failed'
                              : 'Not vectorized for chat'
                      }
                    >
                      {indexedStatus === 'yes' ? (
                        <Check className="h-3.5 w-3.5 shrink-0 stroke-[2.5]" aria-hidden />
                      ) : indexedStatus === 'pending' ? (
                        <Loader className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                      ) : (
                        <X className="h-3.5 w-3.5 shrink-0 stroke-[2.5]" aria-hidden />
                      )}
                      {indexedStatus === 'yes'
                        ? 'Vectorized'
                        : indexedStatus === 'pending'
                          ? 'Vector pending'
                          : indexedStatus === 'failed'
                            ? 'Vector failed'
                            : 'Vector off'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <>
                  <button
                    onClick={() => setIsEditMode(true)}
                    className="flex items-center gap-2 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJsonModal(true)}
                    className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    title="View raw JSON"
                  >
                    <Braces className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleIndexDocument}
                    disabled={isIndexing || indexedStatus === 'pending'}
                    className={
                      indexedStatus === 'yes'
                        ? 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
                        : indexedStatus === 'failed'
                          ? 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-rose-600 text-white hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-500'
                          : 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-slate-800 text-white hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600'
                    }
                  >
                    {isIndexing || indexedStatus === 'pending' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Vectorizing…
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4" />
                        {indexedStatus === 'yes' ? 'Re-vectorize' : 'Vectorize'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleIndexToAlgolia}
                    disabled={isIndexingAlgolia || algoliaStatus === 'pending'}
                    className={
                      algoliaStatus === 'yes'
                        ? 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-700 text-white hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-600'
                        : algoliaStatus === 'failed'
                          ? 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-rose-600 text-white hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-500'
                          : 'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 text-white hover:bg-blue-700'
                    }
                  >
                    {isIndexingAlgolia || algoliaStatus === 'pending' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Syncing to Algolia…
                      </>
                    ) : (
                      <>
                        <Cloud className="w-4 h-4" />
                        {algoliaStatus === 'yes' ? 'Re-sync Algolia' : 'Index to Algolia'}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {isEditMode && (
          <div className="border-b border-slate-200 dark:border-slate-700 px-8 py-4 bg-white dark:bg-slate-900">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={editState}
                  onChange={(e) => setEditState(e.target.value)}
                  placeholder="e.g., California, New York"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Jurisdiction
                </label>
                <input
                  type="text"
                  value={editJurisdiction}
                  onChange={(e) => setEditJurisdiction(e.target.value)}
                  placeholder="e.g., Federal, State, Local"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {(isEditMode || (document.tags && document.tags.length > 0)) && (
          <div className="border-b border-slate-200 dark:border-slate-700 px-8 py-4 bg-white dark:bg-slate-900">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(isEditMode ? editTags : document.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm border border-blue-200 dark:border-blue-800"
                >
                  {tag}
                  {isEditMode && (
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                      aria-label={`Remove ${tag}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
              {isEditMode && (
                <>
                  {showTagInput ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          } else if (e.key === 'Escape') {
                            setShowTagInput(false);
                            setNewTag('');
                          }
                        }}
                        placeholder="Enter tag name"
                        className="px-3 py-1 text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                      <button
                        onClick={handleAddTag}
                        className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowTagInput(false);
                          setNewTag('');
                        }}
                        className="px-3 py-1 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 text-sm transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTagInput(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-full text-sm border border-slate-300 dark:border-slate-600 transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Add Tag
                    </button>
                  )}
                </>
              )}
            </div>
            {isEditMode && editTags.length < SUGGESTED_NONPROFIT_KNOWLEDGE_TAG_NAMES.length && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Suggested tags:</p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED_NONPROFIT_KNOWLEDGE_TAG_NAMES.filter(
                    (tag) => !editTags.includes(tag)
                  ).map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleAddSuggestedTag(tag)}
                      className="px-2.5 py-1 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-xs border border-slate-200 dark:border-slate-600 transition-colors"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-8 py-8">
          {isEditMode ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm min-h-[500px]"
              placeholder="Enter your markdown content..."
            />
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm ring-1 ring-slate-900/5 dark:ring-slate-800/50">
              <article
                className="prose prose-slate max-w-none px-6 py-6 text-[15px] leading-relaxed dark:prose-invert prose-headings:font-semibold prose-headings:tracking-tight prose-p:my-3 prose-li:my-1 prose-table:text-sm prose-pre:rounded-lg prose-pre:border prose-pre:border-slate-200 prose-pre:bg-slate-50 prose-pre:text-slate-800 dark:prose-pre:border-slate-700 dark:prose-pre:bg-slate-950/80 dark:prose-pre:text-slate-100 select-text"
                tabIndex={0}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800 dark:text-sky-400 dark:hover:text-sky-300"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {document.content}
                </ReactMarkdown>
              </article>
            </div>
          )}
        </div>
      </div>

      {showJsonModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowJsonModal(false)}
          role="presentation"
        >
          <div
            className="flex max-h-[min(90vh,40rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-900 dark:ring-1 dark:ring-slate-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="document-json-modal-title"
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <div className="min-w-0">
                <h2
                  id="document-json-modal-title"
                  className="text-lg font-semibold text-slate-900 dark:text-slate-100"
                >
                  Raw JSON
                </h2>
                <p className="mt-1 truncate text-sm text-slate-600 dark:text-slate-400">
                  {document.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowJsonModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <pre className="whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                {JSON.stringify(document, null, 2)}
              </pre>
            </div>
            <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setShowJsonModal(false)}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
